import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { usePatientAuthFlags } from "../../../Hooks/usePatientAuthFlags";
import {
  fetchCurrentStudy,
  resetUserData,
} from "../../../utils/API_SERVICE_FOR_PATIENT_PAGE";

/**
 * Same flow as Menubar “New Patient Report”: reset server study, refresh StudyId, reload.
 */
export function useNewPatientReport() {
  const { t } = useTranslation("common");
  const { isWixAuth, isCreAuth } = usePatientAuthFlags();
  const [, setSearchParams] = useSearchParams();
  const [isResetting, setIsResetting] = useState(false);

  const startNewPatientReport = useCallback(async () => {
    if (!isWixAuth && !isCreAuth) {
      toast.error(
        t("creFloatingNewPatientUnavailable", {
          defaultValue:
            "New patient report is only available when signed in with Wix.",
        }),
      );
      return;
    }
    setIsResetting(true);
    try {
      await resetUserData();
      const latest = await fetchCurrentStudy();
      if (latest?.studyId) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("StudyId", latest.studyId);
            return next;
          },
          { replace: true },
        );
      }
      toast.info(
        t("readyForNewPatientReport", {
          defaultValue: "Ready for new patient report!",
        }),
      );
      window.setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (e) {
      console.error("Failed to reset user data:", e);
      toast.error(
        t("failedToResetUserData", { defaultValue: "Failed to reset user data" }),
      );
      setIsResetting(false);
    }
  }, [isWixAuth, isCreAuth, setSearchParams, t]);

  return { startNewPatientReport, isResetting };
}
